import { CartModal } from "components/CartModal/CartModal";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch } from "react-redux";
import AnnualCatchupIcon from "static/images/AnnualCatchup.svg";
import {
  taskAlarmApis,
  useLazyGetTaskListForServiceTeamQuery,
  useLazyGetTaskListForUserQuery,
  useGetTaskListForUserQuery,
  useGetTaskListForServiceTeamQuery,
} from "store/apis/taskList";
import { AddAnnualCatchup } from "./AddAnnualCatchup/AddAnnualCatchup";

const bookkeepingMonthlyProduct =
  process.env.PUBLIC_BASE_TASK_TEMPLATE_ID_MONTHLY_BOOKKEEPING;

type Props = {
  entityId: string;
  season: string;
};

export const MonthlyBookkeepingUpsell = ({ entityId, season }: Props) => {
  const addBookkeeping = useModal();
  const cartModal = useModal();
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const { isCustomer } = useRoleBasedView();
  const [isRefreshingTasks, setIsRefreshingTasks] = useState(false);

  const { data: userTaskData, isFetching: isUserTaskFetching } =
    useGetTaskListForUserQuery(
      {
        baseTaskKey: bookkeepingMonthlyProduct,
        seasons: season,
        showArchive: false,
      },
      { skip: !isCustomer }
    );
  const { data: cpaTaskList, isFetching: isAdminTaskFetching } =
    useGetTaskListForServiceTeamQuery(
      {
        baseTaskKey: bookkeepingMonthlyProduct,
        seasons: season,
        showArchive: false,
        groupId,
      },
      { skip: isCustomer }
    );
  const taskCount = (userTaskData || cpaTaskList)?.count || 0;
  const isFetching = isUserTaskFetching || isAdminTaskFetching;

  const [fetchUserTasks] = useLazyGetTaskListForUserQuery();
  const [fetchServiceTeamTasks] = useLazyGetTaskListForServiceTeamQuery();

  const startCheckingIfTaskCreated = async (
    resolve: () => void,
    reject: (error: Error) => void,
    totalSeconds = 0
  ) => {
    try {
      const data = isCustomer
        ? await fetchUserTasks({
            baseTaskKey: bookkeepingMonthlyProduct,
            seasons: season,
            showArchive: false,
          }).unwrap()
        : await fetchServiceTeamTasks({
            baseTaskKey: bookkeepingMonthlyProduct,
            seasons: season,
            showArchive: false,
            groupId,
          }).unwrap();

      if (totalSeconds >= 1 * 60 * 1000) {
        reject(new Error("Task creation timed out. Please try again."));
        return;
      }

      if ((data.count || 0) > taskCount) {
        resolve();
      } else {
        setTimeout(
          () =>
            startCheckingIfTaskCreated(resolve, reject, totalSeconds + 2000),
          2000
        );
      }
    } catch (error) {
      reject(
        new Error("Failed to fetch task list. Please check your network.")
      );
    }
  };

  const handleCartClose = async () => {
    try {
      setIsRefreshingTasks(true);
      cartModal.close();
      await new Promise<void>((resolve, reject) =>
        startCheckingIfTaskCreated(resolve, reject)
      );
      dispatch(taskAlarmApis.util.invalidateTags(["TaskList"]));
      successToast({ title: "Filings from the cart have been launched." });
    } catch (error) {
      if (error instanceof Error) {
        alertToast({ message: error.message });
      } else {
        alertToast({ message: "Something went wrong" });
      }
    } finally {
      setIsRefreshingTasks(false);
    }
  };

  if (isRefreshingTasks || isFetching) {
    return (
      <div className="t-h-40 t-w-full t-flex t-items-center t-justify-center">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div className="t-flex t-justify-between t-border t-border-solid t-border-purple-10 t-bg-purple-0 t-rounded-lg t-p-3 t-relative">
      <div className="t-flex t-flex-col t-gap-3 t-w-11/12">
        <div className="t-flex t-flex-col t-gap-1">
          <div className="t-text-subtitle-sm t-text-text-100">
            Let us handle the calculations 🌟
          </div>
          <div className="t-text-body-sm t-text-text-30">
            Haven't closed out the quarter's books? Inkle has got you covered.
          </div>
        </div>

        <div>
          <Button type="button" onClick={addBookkeeping.open}>
            Get Monthly Bookkeeping
          </Button>
        </div>
      </div>

      <img
        src={AnnualCatchupIcon}
        alt=""
        className="t-absolute t-right-10 t-top-7 sm:t-hidden"
        aria-hidden="true"
      />

      {addBookkeeping.isOpen && (
        <AddAnnualCatchup
          isOpen={addBookkeeping.isOpen}
          close={addBookkeeping.close}
          entityId={entityId}
          groupId={groupId}
          productId={bookkeepingMonthlyProduct!}
          currentSeason={Number(season)}
          onAdd={cartModal.open}
          title="Monthly Bookkeeping"
        />
      )}

      {cartModal.isOpen && (
        <CartModal
          open={cartModal.isOpen}
          onClose={cartModal.close}
          entityIdFromProps={entityId}
          shouldRedirectToTask={false}
          handlePaymentSuccess={handleCartClose}
        />
      )}
    </div>
  );
};
